import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import axios from "axios";

import { rutas } from "../../rutas/rutas";
import { getCookie } from "../../utils/getCookies";

const CACHE_MS = 15000;

let benefitCache = null;
let benefitCacheAt = 0;
let benefitRequest = null;

const EMPTY_BENEFIT = {
  active: false,
  reason: "PET_BENEFIT_UNAVAILABLE",
  level: 0,
  stage: "puppy",
  cityFarm: {
    farmTimeReductionPercent: 0,
    waterProductionBonusPercent: 0,
    energyTimeReductionPercent: 0,
    millTimeReductionPercent: 0,
  },
};

function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function normalizeBenefit(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const cityFarm =
    source.cityFarm && typeof source.cityFarm === "object"
      ? source.cityFarm
      : {};

  return {
    active: Boolean(source.active),
    reason:
      source.reason ||
      (source.active ? "ACTIVE" : "PET_BENEFIT_UNAVAILABLE"),
    level: safeNumber(source.level),
    stage: source.stage || "puppy",
    cityFarm: {
      farmTimeReductionPercent: safeNumber(
        cityFarm.farmTimeReductionPercent
      ),
      waterProductionBonusPercent: safeNumber(
        cityFarm.waterProductionBonusPercent
      ),
      energyTimeReductionPercent: safeNumber(
        cityFarm.energyTimeReductionPercent
      ),
      millTimeReductionPercent: safeNumber(
        cityFarm.millTimeReductionPercent
      ),
    },
  };
}

function getPetBenefitUrl() {
  /*
   * Usa rutas.petBenefit cuando existe.
   * Si todavía no fue agregado a rutas.js, usa la ruta relativa segura.
   * Esto evita:
   * Cannot read properties of undefined (reading "protocol")
   */
  const configuredUrl = rutas?.petBenefit;

  if (
    typeof configuredUrl === "string" &&
    configuredUrl.trim().length > 0
  ) {
    return configuredUrl;
  }

  return "/api/pet-benefit/";
}

function getStoredToken() {
  if (typeof window === "undefined") {
    return "";
  }

  return (
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    getCookie("token") ||
    ""
  );
}

async function requestBenefit(force = false) {
  const now = Date.now();

  if (
    !force &&
    benefitCache &&
    now - benefitCacheAt < CACHE_MS
  ) {
    return benefitCache;
  }

  if (!force && benefitRequest) {
    return benefitRequest;
  }

  const endpoint = getPetBenefitUrl();
  const token = getStoredToken();

  benefitRequest = axios
    .get(endpoint, {
      headers: {
        token,
      },
      withCredentials: true,
    })
    .then(({ data }) => {
      const rawBenefit =
        data?.petBenefit ||
        data?.benefit ||
        data?.cityFarmPetBenefit ||
        null;

      const normalized = normalizeBenefit(rawBenefit);

      console.log("[CITY FARM][PET BENEFIT DISPLAY]", {
        endpoint,
        ok: Boolean(data?.ok),
        user: data?.user,
        active: normalized.active,
        reason: normalized.reason,
        level: normalized.level,
        cityFarm: normalized.cityFarm,
      });

      benefitCache = normalized;
      benefitCacheAt = Date.now();

      return normalized;
    })
    .catch((error) => {
      console.error("[CITY FARM][PET BENEFIT DISPLAY ERROR]", {
        endpoint,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });

      /*
       * No mostramos errores técnicos de Axios dentro del juego.
       * La consola conserva el detalle real.
       */
      return normalizeBenefit({
        ...EMPTY_BENEFIT,
        reason:
          error?.response?.data?.error ||
          "PET_BENEFIT_UNAVAILABLE",
      });
    })
    .finally(() => {
      benefitRequest = null;
    });

  return benefitRequest;
}

function getDisplay(type, benefit) {
  const cityFarm = benefit?.cityFarm || EMPTY_BENEFIT.cityFarm;

  switch (type) {
    case "water":
      return {
        value: safeNumber(cityFarm.waterProductionBonusPercent),
        sign: "+",
        defaultLabel: "Extra Water",
      };

    case "energy":
      return {
        value: safeNumber(cityFarm.energyTimeReductionPercent),
        sign: "-",
        defaultLabel: "Energy cycle time",
      };

    case "mill":
      return {
        value: safeNumber(cityFarm.millTimeReductionPercent),
        sign: "-",
        defaultLabel: "Mill processing time",
      };

    case "farm":
    default:
      return {
        value: safeNumber(cityFarm.farmTimeReductionPercent),
        sign: "-",
        defaultLabel: "Production time",
      };
  }
}

function getInactiveText(reason) {
  const messages = {
    PET_NEEDS_CARE: "Pet needs care",
    PET_FALLEN: "Pet must be revived",
    PET_NOT_FOUND: "Pet not found",
    OWNER_REQUIRED: "Player not identified",
    AUTH_REQUIRED: "Login required",
    USER_NOT_FOUND: "City Farm user not found",
    BDB_MONGO_URI_MISSING: "Pet connection unavailable",
    PET_BENEFIT_UNAVAILABLE: "Pet benefit unavailable",
    PET_BENEFIT_ENDPOINT_ERROR: "Pet benefit unavailable",
  };

  return messages[String(reason || "")] || "Pet benefit inactive";
}

export function clearPetBenefitDisplayCache() {
  benefitCache = null;
  benefitCacheAt = 0;
}

export default function PetBenefitDisplay({
  type = "farm",
  label = "",
  icon = "🐾",
}) {
  const [benefit, setBenefit] = useState(() =>
    normalizeBenefit(benefitCache || EMPTY_BENEFIT)
  );
  const [loading, setLoading] = useState(!benefitCache);

  useEffect(() => {
    let mounted = true;

    const refresh = async (force = false) => {
      if (mounted) {
        setLoading(true);
      }

      const nextBenefit = await requestBenefit(force);

      if (mounted) {
        setBenefit(nextBenefit);
        setLoading(false);
      }
    };

    refresh(false);

    const handleRefresh = () => {
      clearPetBenefitDisplayCache();
      refresh(true);
    };

    window.addEventListener(
      "cityfarm-pet-benefit-refresh",
      handleRefresh
    );
    window.addEventListener(
      "pet-benefit-refresh",
      handleRefresh
    );
    window.addEventListener(
      "actualizar-saldos",
      handleRefresh
    );

    return () => {
      mounted = false;

      window.removeEventListener(
        "cityfarm-pet-benefit-refresh",
        handleRefresh
      );
      window.removeEventListener(
        "pet-benefit-refresh",
        handleRefresh
      );
      window.removeEventListener(
        "actualizar-saldos",
        handleRefresh
      );
    };
  }, []);

  const display = getDisplay(type, benefit);
  const finalLabel = label || display.defaultLabel;
  const active = Boolean(benefit.active);

  return (
    <Box
      sx={{
        width: "100%",
        mt: 0.8,
        mb: 0.8,
        px: 1,
        py: 0.65,
        borderRadius: 1,
        border: active
          ? "1px solid rgba(173,255,47,0.30)"
          : "1px solid rgba(255,180,90,0.22)",
        background: active
          ? "linear-gradient(90deg, rgba(70,130,30,0.22), rgba(0,0,0,0.30))"
          : "linear-gradient(90deg, rgba(120,65,20,0.18), rgba(0,0,0,0.30))",
        boxShadow: active
          ? "0 0 14px rgba(173,255,47,0.09)"
          : "none",
        position: "relative",
        zIndex: 5,
      }}
    >
      <Typography
        sx={{
          color: active
            ? "greenyellow"
            : "rgba(255,255,255,0.80)",
          fontSize: "0.76rem !important",
          textAlign: "center",
          textShadow: "0 0 8px rgba(0,0,0,0.45)",
        }}
      >
        {loading
          ? `${icon} Loading pet benefit...`
          : active
          ? `${icon} PET LVL ${benefit.level} · ${finalLabel} ${display.sign}${display.value}%`
          : `${icon} ${getInactiveText(benefit.reason)}`}
      </Typography>
    </Box>
  );
}
